import React, { useCallback, useState } from 'react';
import { Button } from 'reactstrap';
import { useLazyKbSearchQuery } from 'src/rtk/features/chatbot/chatbotApi';
import ChatInput from './ChatInput';
import ChatMessageList from './ChatMessageList';

const FALLBACK_MESSAGE = "I couldn't find an exact match for your question. Try rephrasing or choose one of the related questions below.";
const RELATED_TOP_N = 3;

interface ChatWindowProps {
  onClose: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [triggerSearch, { isLoading }] = useLazyKbSearchQuery();

  const addMessage = useCallback((role: 'user' | 'bot', content: string, opts?: { relatedQuestions?: { question: string; id: number }[]; isFallback?: boolean }) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        role,
        content,
        relatedQuestions: opts?.relatedQuestions,
        isFallback: opts?.isFallback,
        timestamp: new Date(),
      },
    ]);
  }, []);

  const handleSubmit = useCallback(
    async (text: string) => {
      addMessage('user', text);
      try {
        const result = await triggerSearch({ query: text, limit: 5 }).unwrap();
        const data = result?.data;
        if (!data) {
          addMessage('bot', FALLBACK_MESSAGE, { isFallback: true, relatedQuestions: [] });
          return;
        }
        const hasMatch = data.has_match && data.best_match;
        const answer = hasMatch ? data.best_match!.answer : FALLBACK_MESSAGE;
        const related = (data.related_questions ?? []).slice(0, RELATED_TOP_N).map((r) => ({ question: r.question, id: r.id }));
        addMessage('bot', answer, {
          isFallback: !hasMatch,
          relatedQuestions: related.length > 0 ? related : undefined,
        });
      } catch (e) {
        addMessage('bot', 'Sorry, something went wrong. Please try again later.', { isFallback: true });
      }
    },
    [triggerSearch, addMessage]
  );

  const handleRelatedClick = useCallback(
    (question: string, _id: number) => {
      handleSubmit(question);
    },
    [handleSubmit]
  );

  return (
    <div className="chatbot-window">
      <div className="chatbot-window-header d-flex justify-content-between align-items-center">
        <span className="chatbot-window-title">Question & Answer</span>
        <Button close className="chatbot-close" onClick={onClose} aria-label="Close" />
      </div>
      <div className="chatbot-window-body">
        <ChatMessageList messages={messages} onRelatedClick={handleRelatedClick} />
        {isLoading && (
          <div className="chatbot-message chatbot-message--bot">
            <div className="chatbot-message-bubble">
              <span className="chatbot-typing">Searching...</span>
            </div>
          </div>
        )}
      </div>
      <div className="chatbot-window-footer">
        <ChatInput onSubmit={handleSubmit} disabled={isLoading} />
      </div>
    </div>
  );
};

export default ChatWindow;
